import React, { createContext, useState } from 'react';
import {
  ClassificationDto,
  CostCodeDto,
  JobSummaryDto,
  PayCodeDto,
} from 'src/api/FiretrolApiClient';
import { PropsWithChildren } from 'src/PropsWithChildren';

interface IDialogsContext {
  createProjectOpen: boolean;
  setCreateProjectOpen: (isOpen: boolean) => void;
  scopeInfoOpen: boolean;
  setScopeInfoOpen: (isOpen: boolean) => void;
  createCostCodeDialogOpen: boolean;
  setCreateCostCodeDialogOpen: (
    isOpen: boolean,
    costCode?: CostCodeDto,
  ) => void;
  selectedCostCode: CostCodeDto | undefined;
  setCreateJobDialogOpen: (job: JobSummaryDto | null) => void;
  ctcJob: JobSummaryDto | null;
  addPayCodeOpen: boolean;
  setAddPayCodeOpen: (isOpen: boolean, payCode?: PayCodeDto) => void;
  selectedPayCode: PayCodeDto | undefined;
  refreshPayCodes: () => void;
  refreshPayCodesCounter: number;
  addClassificationOpen: boolean;
  setAddClassificationOpen: (
    isOpen: boolean,
    classification?: ClassificationDto,
  ) => void;
  selectedClassification: ClassificationDto | undefined;
  refreshClassifications: () => void;
  refreshClassificationsCounter: number;
}

export const DialogContext = createContext<IDialogsContext>({
  createProjectOpen: false,
  setCreateProjectOpen: () => null,
  scopeInfoOpen: false,
  setScopeInfoOpen: () => null,
  createCostCodeDialogOpen: false,
  setCreateCostCodeDialogOpen: () => null,
  selectedCostCode: undefined,
  setCreateJobDialogOpen: () => null,
  ctcJob: null,
  addPayCodeOpen: false,
  setAddPayCodeOpen: () => null,
  selectedPayCode: undefined,
  refreshPayCodes: () => null,
  refreshPayCodesCounter: 0,
  addClassificationOpen: false,
  setAddClassificationOpen: () => null,
  selectedClassification: undefined,
  refreshClassifications: () => null,
  refreshClassificationsCounter: 0,
});

export const DialogsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [createProjectOpen, setCreateProjectOpen] = useState<boolean>(false);
  const [scopeInfoOpen, setScopeInfoOpen] = useState<boolean>(false);

  const [ctcJob, setCtcJob] = React.useState<JobSummaryDto | null>(null);

  const [createCostCodeDialogOpen, setCreateCostCodeDialog] =
    useState<boolean>(false);
  const [selectedCostCode, setSelectedCostCode] = useState<
    CostCodeDto | undefined
  >(undefined);

  const [addPayCodeOpen, setAddPayCode] = useState<boolean>(false);
  const [selectedPayCode, setSelectedPayCode] = useState<
    PayCodeDto | undefined
  >(undefined);
  const [refreshPayCodesCounter, setRefreshPayCodesCounter] = useState(0);

  const [addClassificationOpen, setAddClassification] =
    useState<boolean>(false);
  const [selectedClassification, setSelectedClassification] = useState<
    ClassificationDto | undefined
  >(undefined);
  const [refreshClassificationsCounter, setRefreshClassificationsCounter] =
    useState(0);

  React.useEffect(() => {
    if (!createProjectOpen) {
      setCtcJob(null);
    }
  }, [createProjectOpen]);
  const setCreateJobDialogOpen = (job: JobSummaryDto | null) => {
    setCtcJob(job);
    setCreateProjectOpen(true);
  };

  const setCreateCostCodeDialogOpen = (
    isOpen: boolean,
    costCode?: CostCodeDto,
  ) => {
    setCreateCostCodeDialog(isOpen);
    setSelectedCostCode(costCode);
  };

  const setAddPayCodeOpen = (isOpen: boolean, payCode?: PayCodeDto) => {
    setAddPayCode(isOpen);
    setSelectedPayCode(payCode);
  };

  const refreshPayCodes = () => {
    setRefreshPayCodesCounter((prev) => prev + 1);
  };

  const setAddClassificationOpen = (
    isOpen: boolean,
    classification?: ClassificationDto,
  ) => {
    setAddClassification(isOpen);
    setSelectedClassification(classification);
  };

  const refreshClassifications = () => {
    setRefreshClassificationsCounter((prev) => prev + 1);
  };

  return (
    <DialogContext.Provider
      value={{
        createProjectOpen,
        setCreateProjectOpen,
        scopeInfoOpen,
        setScopeInfoOpen,
        createCostCodeDialogOpen,
        setCreateCostCodeDialogOpen,
        selectedCostCode,
        setCreateJobDialogOpen,
        ctcJob,
        addPayCodeOpen,
        setAddPayCodeOpen,
        selectedPayCode,
        refreshPayCodes,
        refreshPayCodesCounter,
        addClassificationOpen,
        setAddClassificationOpen,
        selectedClassification,
        refreshClassifications,
        refreshClassificationsCounter,
      }}
    >
      {children}
    </DialogContext.Provider>
  );
};

export const useDialogs: () => IDialogsContext = () => {
  return React.useContext(DialogContext);
};
